import React from "react";
import { useNavigate } from "react-router-dom";
import IconProvider from "@/components/ui/IconProvider";
import FolderTree from "../FolderTree";
import {
  SidebarGroup,
  SidebarGroupContent,
} from "@/components/layout/side-bars/sidebar";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useTabContext } from "@/contexts/TabContext";
import ContextMenu from "@/components/ui/ContextMenu";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

// Import types
import { FolderStructureProps } from './types';

// Import constants and helpers
import { getRouteForItemType, getLabelForItemType } from './constants';

// Import hooks
import { useFolderData } from './hooks/useFolderData';
import { useContextMenu } from './hooks/useContextMenu';
import { useMultiSelect } from './hooks/useMultiSelect';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useBatchOperations } from './hooks/useBatchOperations';

export default function FolderStructure({
  folders,
  selectedFolderId,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderColorChange,
  onItemDrop,
  onFolderMove,
  className = "",
  showItemCounts = false,
  allowCreation = true,
  allowRename = true,
  allowDelete = true,
  enableDragDrop = true,
  chevronSize = 12,
  showFolderIcon = true,
  folderIconSize = 14,
  title = "Folders",
  isLoading = false,
  // LinkEngine integration props
  gameId,
  itemType = 'glossary',
  // Item handling props
  showItems = true,
  onItemSelect,
  selectedItemId,
  // Item creation props
  oncreateObject,
  onCreateDocument,
  onCreateBlueprint,
  onCreateDialog,
  onCreateFlow,
  onCreateVariable,
  // Item actions
  onItemRename,
  onItemDelete,
  onItemDuplicate,
  // Auto-edit props
  newlyCreatedFolderId,
  onClearNewlyCreatedFolder,
}: FolderStructureProps) {
  const { t } = usePlatformTranslation();
  const userData = useSimplifiedUserData();
  const navigate = useNavigate();
  const { openTab } = useTabContext();

  // Use custom hooks for data fetching
  const {
    finalFolders,
    rootItemCount,
    sortedRootFolderWithItems,
    isLoadingCombined,
  } = useFolderData({
    gameId,
    itemType,
    folders,
    showItems,
    isLoading,
  });

  // Use multi-select hook
  const {
    selectedEntities,
    lastSelectedEntity,
    handleEntityClick,
    setSelectedEntities,
    setLastSelectedEntity,
  } = useMultiSelect({
    onFolderSelect,
    onItemSelect,
  });

  // Use drag and drop hook
  const {
    dragOverFolderId,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    enableDragDrop,
    onItemDrop,
  });

  // Build all folders list for batch operations
  const allFolders = [...finalFolders];
  if (sortedRootFolderWithItems && sortedRootFolderWithItems.children) {
    allFolders.unshift(sortedRootFolderWithItems);
  }

  // Use batch operations hook
  const { handleBatchDelete, handleBatchMove } = useBatchOperations({
    selectedEntities,
    setSelectedEntities,
    setLastSelectedEntity,
    onFolderDelete,
    onItemDelete,
    onFolderMove,
    onItemDrop,
  });

  // Use context menu hook
  const {
    contextMenu,
    addButtonRef,
    handleAddButtonClick,
    closeContextMenu,
    getContextMenuItems,
  } = useContextMenu({
    selectedEntities,
    setSelectedEntities,
    setLastSelectedEntity,
    allowCreation,
    itemType,
    onFolderCreate,
    oncreateObject,
    onCreateBlueprint,
    onCreateDocument,
    onCreateDialog,
    onCreateFlow,
    onCreateVariable,
    handleBatchDelete: () => handleBatchDelete(allFolders),
  });

  // Handle "Show All" button click - opens in a new tab
  const handleShowAll = () => {
    const path = getRouteForItemType(itemType);
    const label = getLabelForItemType(itemType);
    
    // Open the tab (icon will be auto-determined by TabContext based on path)
    openTab({
      id: path,
      title: label,
      path: path,
    });
    
    // Navigate to the path
    navigate(path);
  };

  return (
    <div
      className={`w-64 bg-sidebar overflow-y-auto animate-in fade-in duration-200 ${className}`}
    >
      <div className="pt-1">
        <SidebarGroup>
          {title && (
            <div className="flex items-center gap-2">
              <h4 className="text-base font-bold text-foreground m-0 leading-none">{title}</h4>
              {allowCreation && (
                <button
                  ref={addButtonRef}
                  onClick={handleAddButtonClick}
                  className="p-1 hover:bg-sidebar-accent rounded text-sidebar-accent-foreground flex items-center justify-center"
                  title={t('platform.layout.side-bars.add_folder_or_item')}
                >
                  <IconProvider iconstring="fa-plus" size={16} />
                </button>
              )}
            </div>
          )}

          <SidebarGroupContent className={title ? "pt-9" : ""}>
            {/* Multi-select info banner */}
            {selectedEntities.size > 0 && (
              <div className="mb-2 p-2 bg-sidebar-accent rounded text-xs text-sidebar-accent-foreground">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium">{selectedEntities.size} selected</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setSelectedEntities(new Set())}
                      className="px-2 py-1 hover:bg-sidebar rounded"
                      title="Clear selection"
                    >
                      <IconProvider iconstring="fa-times" size={12} />
                    </button>
                    {allowDelete && (
                      <button
                        onClick={() => handleBatchDelete(allFolders)}
                        className="px-2 py-1 hover:bg-sidebar rounded text-destructive"
                        title="Delete selected"
                      >
                        <IconProvider iconstring="fa-trash" size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-[10px] opacity-75 flex items-center gap-1">
                  <IconProvider iconstring="fa-arrow-right-arrow-left" size={10} />
                  <span>Drag to move all selected items to a folder</span>
                </div>
              </div>
            )}
            
            {/* Folder tree */}
            <FolderTree
              folders={finalFolders}
              selectedFolderId={selectedFolderId}
              onFolderSelect={onFolderSelect}
              onFolderCreate={onFolderCreate}
              onFolderRename={onFolderRename}
              onFolderDelete={onFolderDelete}
              onFolderColorChange={onFolderColorChange}
              onItemDrop={onItemDrop}
              onFolderMove={onFolderMove}
              showItemCounts={showItemCounts}
              allowCreation={allowCreation}
              allowRename={allowRename}
              allowDelete={allowDelete}
              enableDragDrop={enableDragDrop}
              chevronSize={chevronSize}
              showFolderIcon={showFolderIcon}
              folderIconSize={folderIconSize}
              isLoading={isLoadingCombined}
              showItems={showItems}
              onItemSelect={onItemSelect}
              selectedItemId={selectedItemId}
              itemType={itemType}
              oncreateObject={oncreateObject}
              onCreateDocument={onCreateDocument}
              onCreateBlueprint={onCreateBlueprint}
              onCreateDialog={onCreateDialog}
              onCreateFlow={onCreateFlow}
              onCreateVariable={onCreateVariable}
              onItemRename={onItemRename}
              onItemDelete={onItemDelete}
              onItemDuplicate={onItemDuplicate}
              rootFolderWithItems={showItems ? sortedRootFolderWithItems : null}
              {...(showItemCounts && { rootItemCount })}
              selectedEntities={selectedEntities}
              onEntityClick={handleEntityClick}
              onBatchMove={(targetFolderId) => handleBatchMove(targetFolderId, allFolders)}
              newlyCreatedFolderId={newlyCreatedFolderId}
              onClearNewlyCreatedFolder={onClearNewlyCreatedFolder}
            />
          </SidebarGroupContent>
        </SidebarGroup>
      </div>

      {/* Context Menu for + button - only show when title is present (standalone mode) */}
      {title && (
        <ContextMenu
          isOpen={contextMenu.isOpen}
          position={contextMenu.position}
          items={getContextMenuItems()}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
}
